import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../AuthContext";
import { Logo } from "../components/Logo";
import { toast } from "sonner";
import {
  ArrowRight, Video, Clock, Users, CalendarDays, Sparkles,
  Check, Play, Lock, GraduationCap, ChevronRight, Loader2,
  Youtube, Globe, MapPin, BookOpen, PenLine, Mail, UserCheck,
} from "lucide-react";

const YOUTUBE_URL = "https://www.youtube.com/@NeuroeQ4Parents";
const CONTACT_EMAIL = "contractneuroeq@gmail.com";

// Compute the next 1st-Tuesday-of-the-month (today or in the future).
function nextFirstTuesday(from = new Date()) {
  const d = new Date(from.getFullYear(), from.getMonth(), 1);
  // Advance to the first Tuesday of this month
  while (d.getDay() !== 2) d.setDate(d.getDate() + 1);
  // If that date is already in the past this month, roll to next month
  if (d < new Date(from.getFullYear(), from.getMonth(), from.getDate())) {
    d.setMonth(d.getMonth() + 1, 1);
    while (d.getDay() !== 2) d.setDate(d.getDate() + 1);
  }
  return d;
}

function formatTrialDate(d) {
  return d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric",
                                           month: "long", year: "numeric" });
}

const EXPLORATION_ANGLES = [
  { emoji: "🧭", label: "Self-awareness" },
  { emoji: "💡", label: "Life skills" },
  { emoji: "🤝", label: "Values & character" },
  { emoji: "🌍", label: "Geography & world" },
  { emoji: "🧪", label: "Science" },
  { emoji: "💰", label: "Economy" },
  { emoji: "🗞️", label: "Current affairs" },
  { emoji: "🧠", label: "Critical thinking" },
];

const BRING_ITEMS = [
  { icon: MapPin,   label: "India map" },
  { icon: Globe,    label: "Global map" },
  { icon: BookOpen, label: "Dictionary (age 9+)" },
  { icon: PenLine,  label: "Notebook & pen" },
];

const PRODUCT_CARDS = [
  { key: "jidnyasa", label: "JJLP only",
    tagline: "Just Jidnyasa Jr Leaders live weekly classes",
    bullets: ["Live 60-min weekly class · Knowledge of Self + World",
               "Session recordings for subscribers",
               "Small live cohort, real teachers"] },
  { key: "both",     label: "Both programs (Joint)",
    tagline: "Best value · JJLP + NGP",
    highlight: true,
    bullets: ["Everything in JJLP",
               "Everything in NGP",
               "One subscription, one bill"] },
  { key: "neuroeq",  label: "NGP only",
    tagline: "Parent-led Sunday ritual (age 4–13)",
    bullets: ["Personalized story every Sunday",
               "Guided parent-child questions",
               "One activity + reflection weekly"] },
];

const BENEFITS = [
  { icon: Video,         title: "4 live classes a month",
    body: "Live weekly class. Interactive, small-batch — real teachers, real conversations." },
  { icon: GraduationCap, title: "Age 7–13",
    body: "Curated topics that spark curiosity at the right stage." },
  { icon: Users,         title: "Small live cohort",
    body: "Kids ask questions, share ideas, learn from each other." },
  { icon: Play,          title: "Recordings + materials",
    body: "Miss a session? Recording + notes available to subscribers." },
];

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    weekday: "long", day: "numeric", month: "long",
    hour: "numeric", minute: "2-digit", hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

export default function Jidnyasa() {
  const { me } = useAuth();
  const nav = useNavigate();
  const [upcoming, setUpcoming] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState(null);
  const [interval, setInterval] = useState("yearly");

  useEffect(() => {
    api.get("/jidnyasa/upcoming").then((r) => setUpcoming(r.data.session))
       .catch(() => {}).finally(() => setLoading(false));
    api.get("/billing/pricing").then((r) => setPricing(r.data.plans))
       .catch(() => {});
  }, []);

  const goSubscribe = (productKey) => {
    const src = productKey || "jidnyasa";
    const qs = `src=${src}&interval=${interval}`;
    if (!me) return nav(`/login?next=${encodeURIComponent(`/paywall?${qs}`)}`);
    nav(`/paywall?${qs}`);
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="jidnyasa-page">
      {/* Header */}
      <header className="max-w-5xl mx-auto px-6 sm:px-8 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Logo size={96} />
        </Link>
        <div className="flex items-center gap-3">
          {me ? (
            <Link to="/jidnyasa/dashboard" className="text-sm font-medium text-[hsl(var(--neu-orange))] hover:underline"
                  data-testid="jidnyasa-dashboard-link">
              My Dashboard →
            </Link>
          ) : (
            <Link to="/login" className="text-sm text-[hsl(var(--neu-ink))] hover:underline"
                  data-testid="jidnyasa-signin-link">
              Sign in
            </Link>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-10">
        <div className="text-[10px] uppercase tracking-widest text-white bg-[hsl(var(--neu-green))] inline-block px-2.5 py-1 rounded-full font-medium mb-4">
          JJLP · Jidnyasa Jr Leaders Program
        </div>
        <h1 className="font-display text-5xl sm:text-6xl leading-[1.05] tracking-tight max-w-3xl">
          <span className="text-[hsl(var(--neu-ink))]">A live leadership class for </span>
          <span className="italic text-[hsl(var(--neu-green))]">curious kids</span>
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
          A live 60-minute class every week with two parts in one session —
          <b> Knowledge of Self</b> (character, values, self-awareness) and
          <b> Knowledge of World</b> (science, geography, current affairs, critical thinking).
          Real teachers, small groups, real conversations.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[hsl(var(--neu-green))]/30 text-sm">
            <GraduationCap className="w-3.5 h-3.5 text-[hsl(var(--neu-green))]"/> Ages 7–13
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[hsl(var(--neu-green))]/30 text-sm">
            <CalendarDays className="w-3.5 h-3.5 text-[hsl(var(--neu-green))]"/> Live weekly class · evenings IST
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[hsl(var(--neu-green))]/30 text-sm">
            <Clock className="w-3.5 h-3.5 text-[hsl(var(--neu-green))]"/> 60 min · Self + World in one session
          </span>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={() => goSubscribe("jidnyasa")} className="btn-jidnyasa text-lg"
                  data-testid="jidnyasa-subscribe-btn">
            Subscribe to JJLP · ₹499/mo <ArrowRight className="w-4 h-4"/>
          </button>
          <a href={`mailto:${CONTACT_EMAIL}?subject=Free%20trial%20seat%20for%20JJLP&body=Hi%20team%2C%0A%0AI%27d%20like%20to%20attend%20the%20free%20trial%20class%20on%20${encodeURIComponent(formatTrialDate(nextFirstTuesday()))}.%0A%0AParent%20name%3A%0AChild%27s%20age%3A%0ACity%3A%0A%0AThanks%21`}
              className="btn-ghost text-lg inline-flex items-center gap-2 border-2 border-[hsl(var(--neu-green))]/40 text-[hsl(var(--neu-green-deep))] hover:bg-[hsl(var(--neu-green-soft))]"
              data-testid="jidnyasa-trial-btn">
            <UserCheck className="w-4 h-4"/> Request a free trial seat
          </a>
          {!me && (
            <Link to="/login?next=/jidnyasa/dashboard" className="btn-ghost text-lg"
                  data-testid="jidnyasa-login-btn">
              Login <ChevronRight className="w-4 h-4"/>
            </Link>
          )}
        </div>
      </section>

      {/* Free trial banner — every 1st Tuesday of the month */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-4">
        <div className="rounded-3xl bg-gradient-to-br from-[hsl(var(--neu-green))]/15 via-white to-[hsl(var(--neu-green))]/5 border-2 border-[hsl(var(--neu-green))]/25 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4"
              data-testid="jidnyasa-trial-banner">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-white bg-[hsl(var(--neu-green))] inline-block px-2.5 py-1 rounded-full font-medium">
              Free trial · every 1st Tuesday
            </div>
            <div className="font-display text-xl sm:text-2xl mt-3 leading-tight">
              Next free trial class · {formatTrialDate(nextFirstTuesday())}
            </div>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Bring your child along for a free live session before you subscribe. Email us to reserve a seat — we'll share the join link.
            </p>
          </div>
          <a href={`mailto:${CONTACT_EMAIL}?subject=Free%20trial%20seat%20for%20JJLP%20on%20${encodeURIComponent(formatTrialDate(nextFirstTuesday()))}&body=Hi%20team%2C%0A%0AI%27d%20like%20to%20attend%20the%20free%20JJLP%20trial%20class%20on%20${encodeURIComponent(formatTrialDate(nextFirstTuesday()))}.%0A%0AParent%20name%3A%0AChild%27s%20name%20%26%20age%3A%0ACity%3A%0A%0AThanks%21`}
              className="btn-jidnyasa text-base shrink-0"
              data-testid="jidnyasa-trial-request-btn">
            <Mail className="w-4 h-4"/> Request a trial seat
          </a>
        </div>
        <p className="text-xs text-muted-foreground mt-2 pl-2">
          Reach us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-[hsl(var(--neu-green-deep))] font-medium hover:underline">{CONTACT_EMAIL}</a> for anything about JJLP — joining, trial seats, or questions.
        </p>
      </section>

      {/* Upcoming topic (public) */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-6">
        <div className="rounded-3xl bg-gradient-to-br from-[hsl(var(--neu-orange))]/10 via-white to-[hsl(var(--neu-orange))]/5 border border-[hsl(var(--neu-orange))]/20 p-6 sm:p-8">
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium">
            Upcoming session
          </div>
          {loading ? (
            <div className="mt-3 text-muted-foreground text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading…
            </div>
          ) : upcoming ? (
            <>
              <h2 className="font-display text-2xl sm:text-3xl mt-2" data-testid="jidnyasa-upcoming-topic">
                {upcoming.topic}
              </h2>
              {upcoming.description && (
                <p className="text-muted-foreground mt-2 max-w-2xl">{upcoming.description}</p>
              )}
              <p className="text-sm text-[hsl(var(--neu-ink))] mt-3 font-medium">
                <CalendarDays className="w-4 h-4 inline mr-1.5 -mt-0.5 text-[hsl(var(--neu-orange))]"/>
                {formatDate(upcoming.scheduled_at)}
              </p>
            </>
          ) : (
            <p className="mt-3 text-muted-foreground">
              Next topic is being planned — subscribe now to be there when it goes live.
            </p>
          )}
        </div>
      </section>

      {/* What is Jidnyasa? */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-14 sm:py-20">
        <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium mb-3">
          What is JJLP?
        </div>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight max-w-3xl">
          Two halves of leadership. One live hour. Every week.
        </h2>
        <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">
          Each 60-minute live class is split into two parts: <b>Knowledge of Self</b> — character, values,
          emotional honesty, communication, self-awareness — and <b>Knowledge of World</b> — science,
          geography, economy, current affairs, critical thinking. Together they build the muscles of
          young leaders: curiosity, perspective and conviction — the same ones that pay off later in
          <b> UPSC prep, entrepreneurship, and life</b>.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          <div className="rounded-2xl p-4 bg-white border-l-4 border-[hsl(var(--neu-green))]">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-green-deep))] font-medium">
              Part 1 · Knowledge of Self
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Self-awareness, values, honest reflection, communication — the inner compass of a leader.
            </p>
          </div>
          <div className="rounded-2xl p-4 bg-white border-l-4 border-[hsl(var(--neu-orange))]">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium">
              Part 2 · Knowledge of World
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Science, geography, economy, current affairs, critical thinking — the outward lens of a leader.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-6">
          {EXPLORATION_ANGLES.map((a) => (
            <span key={a.label}
                   className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-black/5 text-sm">
              <span aria-hidden>{a.emoji}</span> {a.label}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          {/* What to bring */}
          <div className="card-soft p-6" data-testid="jidnyasa-bring-card">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium">
              What your child needs
            </div>
            <div className="font-display text-xl mt-1">Keep these ready each week</div>
            <ul className="mt-4 space-y-2 text-sm">
              {BRING_ITEMS.map((b) => {
                const Icon = b.icon;
                return (
                  <li key={b.label} className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-[hsl(var(--neu-orange))] shrink-0"/>
                    <span>{b.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* YouTube + Founder */}
          <div className="card-soft p-6 flex flex-col" data-testid="jidnyasa-founder-card">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium">
              Watch past sessions
            </div>
            <div className="font-display text-xl mt-1">Jidnyasa · Building Leaders</div>
            <p className="text-sm text-muted-foreground mt-2">
              A glimpse of what a live class feels like — free replays on YouTube.
            </p>
            <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer"
                className="btn-jidnyasa text-sm w-fit mt-4"
                data-testid="jidnyasa-youtube-btn">
              <Youtube className="w-4 h-4"/> Open YouTube channel
            </a>
            <div className="mt-auto pt-5 border-t border-black/5 mt-6">
              <div className="text-xs text-muted-foreground">Led by</div>
              <div className="font-medium mt-1">Ganesh Birale</div>
              <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                BE · MBA (IIT Madras) · LLB (ILS Law College) · Ex-Google · Business person &amp; investor.
                <span className="block mt-1 italic">
                  &ldquo;I&apos;m doing this for my daughter — and I&apos;m inviting other curious kids too.&rdquo;
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-14 sm:py-20">
        <h2 className="font-display text-3xl sm:text-4xl leading-tight">
          What&apos;s included
        </h2>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Live learning, at a pace that fits busy weeknights.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="card-soft p-5 flex items-start gap-4"
                    data-testid={`jidnyasa-benefit-${i}`}>
                <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--neu-orange))]/10 flex items-center justify-center shrink-0">
                  <Icon className="w-6 h-6 text-[hsl(var(--neu-orange))]" />
                </div>
                <div>
                  <div className="font-display text-xl">{b.title}</div>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{b.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-14 sm:py-20">
        <h2 className="font-display text-3xl sm:text-4xl leading-tight">Pricing</h2>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Subscribe to Jidnyasa, NeuroEQ, or bundle both. Cancel any product independently, any time.
        </p>

        {/* Monthly / Yearly toggle */}
        <div className="mt-6 inline-flex items-center gap-1 p-1 rounded-full bg-white/70 border border-black/5"
              data-testid="jidnyasa-pricing-toggle">
          {[
            { k: "yearly",  label: "Yearly · save 20%" },
            { k: "monthly", label: "Monthly" },
          ].map((o) => (
            <button key={o.k} onClick={() => setInterval(o.k)}
                     className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                       interval === o.k ? "bg-[hsl(var(--neu-ink))] text-white shadow" : "text-muted-foreground"
                     }`}
                     data-testid={`jidnyasa-interval-${o.k}`}>
              {o.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {PRODUCT_CARDS.map((p) => {
            const price = pricing?.[p.key]?.[interval];
            return (
              <div key={p.key}
                    className={`rounded-3xl p-6 relative transition-all ${
                      p.highlight
                        ? "bg-[hsl(var(--neu-ink))] text-white shadow-2xl ring-1 ring-[hsl(var(--neu-orange))]/40"
                        : "card-soft"
                    }`}
                    data-testid={`jidnyasa-plan-${p.key}-${interval}`}>
                {p.highlight && (
                  <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-widest bg-[hsl(var(--neu-orange))] text-white px-2 py-1 rounded-full font-medium">
                    Best value
                  </div>
                )}
                <div className={`text-xs uppercase tracking-widest ${p.highlight ? "text-white/70" : "text-muted-foreground"}`}>
                  {p.label}
                </div>
                <div className="font-display text-4xl mt-2 flex items-baseline gap-2">
                  <span>{price ? `₹${price.final_inr.toLocaleString("en-IN")}` : "…"}</span>
                  <span className={`text-sm font-normal ${p.highlight ? "text-white/70" : "text-muted-foreground"}`}>
                    / {interval === "yearly" ? "year" : "month"}
                  </span>
                </div>
                {interval === "yearly" && price && (
                  <div className={`text-xs mt-1 ${p.highlight ? "text-white/75" : "text-muted-foreground"}`}>
                    That&apos;s ~₹{price.per_month_inr}/month · save 20%
                  </div>
                )}
                <p className={`text-xs mt-2 ${p.highlight ? "text-white/75" : "text-muted-foreground"}`}>
                  {p.tagline}
                </p>
                <ul className={`mt-5 space-y-2 text-sm ${p.highlight ? "text-white/85" : "text-[hsl(var(--neu-ink))]"}`}>
                  {p.bullets.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.highlight ? "text-[hsl(var(--neu-orange))]" : "text-[hsl(var(--neu-green))]"}`}/>
                      {item}
                    </li>
                  ))}
                </ul>
                <button onClick={() => goSubscribe(p.key)}
                         className={`mt-6 w-full py-2.5 rounded-full font-medium inline-flex items-center justify-center gap-1 transition-all ${
                           p.key === "jidnyasa"
                             ? "bg-[hsl(var(--neu-green))] text-white hover:bg-[hsl(var(--neu-green-deep))]"
                             : p.highlight
                             ? "bg-[hsl(var(--neu-orange))] text-white hover:brightness-110"
                             : "bg-[hsl(var(--neu-ink))] text-white hover:brightness-110"
                         }`}
                         data-testid={`jidnyasa-choose-${p.key}-${interval}`}>
                  Choose {p.label} <ArrowRight className="w-4 h-4"/>
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="max-w-3xl mx-auto px-6 sm:px-8 py-16 text-center">
        <Sparkles className="w-6 h-6 mx-auto text-[hsl(var(--neu-green))]"/>
        <h2 className="font-display text-3xl sm:text-4xl mt-3">
          Curiosity, once a week — for the whole family.
        </h2>
        <button onClick={() => goSubscribe("jidnyasa")} className="btn-jidnyasa min-w-[260px] text-lg mt-6 mx-auto"
                data-testid="jidnyasa-final-cta">
          Subscribe to JJLP · ₹499/mo <ArrowRight className="w-4 h-4"/>
        </button>
        <p className="text-xs text-muted-foreground mt-3">
          <Lock className="w-3 h-3 inline -mt-0.5 mr-1"/>
          Live meeting link is protected — only active subscribers can join.
        </p>
      </section>
    </div>
  );
}
